import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Typography,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  usePaymentTerms,
  useCreatePaymentTerm,
  useUpdatePaymentTerm,
  useDeletePaymentTerm,
} from '@/hooks/usePaymentTerms';
import type { PaymentTerm } from '@/services/paymentTerm.service';
import { PURCHASE_ORDER_PAYMENT_TERM_MILESTONE } from '@/constants/purchaseOrderPaymentTerms';

const { Title, Text } = Typography;

const PaymentTermsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [form] = Form.useForm();

  const filters = { is_active: activeFilter };
  const { data: list = [], isLoading } = usePaymentTerms(filters);
  const createMutation = useCreatePaymentTerm();
  const updateMutation = useUpdatePaymentTerm();
  const deleteMutation = useDeletePaymentTerm();

  const handleAdd = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, days: 0 });
    setModalOpen(true);
  };

  const handleAddMilestonePreset = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      term_name: 'Milestone 35/30/15/20',
      days: 0,
      description: PURCHASE_ORDER_PAYMENT_TERM_MILESTONE,
      is_active: true,
    });
    setModalOpen(true);
  };

  const handleEdit = (record: PaymentTerm) => {
    setEditingId(record.id);
    form.setFieldsValue({
      term_name: record.term_name,
      days: record.days ?? 0,
      description: record.description ?? '',
      is_active: record.is_active,
    });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        term_name: values.term_name,
        days: values.days ?? 0,
        description: values.description?.trim() || undefined,
        is_active: values.is_active,
      };
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setModalOpen(false);
      form.resetFields();
    } catch (e) {
      if (e && typeof (e as { errorFields?: unknown }).errorFields !== 'undefined') return;
      message.error((e as Error)?.message || t('saveFailed'));
    }
  };

  const columns: ColumnsType<PaymentTerm> = [
    { title: t('name'), dataIndex: 'term_name', key: 'term_name', width: 200, ellipsis: true },
    { title: t('days'), dataIndex: 'days', key: 'days', width: 80 },
    {
      title: t('documentText'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v: string | null | undefined, r) => (
        <Text type="secondary" ellipsis>
          {(v && v.trim()) || r.term_name}
        </Text>
      ),
    },
    {
      title: t('active'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? tc('yes') : tc('no')}</Tag>,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            {tc('edit')}
          </Button>
          <Popconfirm
            title={t('confirmDeletePaymentTerm')}
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText={tc('yes')}
            cancelText={tc('no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {tc('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Title level={4} style={{ margin: 0 }}>
            {t('paymentTermsMaster')}
          </Title>
          <Select
            placeholder={t('status')}
            allowClear
            style={{ width: 120 }}
            value={activeFilter === undefined ? undefined : activeFilter}
            onChange={(v) => setActiveFilter(v)}
            options={[
              { value: true, label: t('active') },
              { value: false, label: t('inactive') },
            ]}
          />
          <Button onClick={handleAddMilestonePreset}>{t('addMilestonePreset')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('addPaymentTerm')}
          </Button>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {t('paymentTermsHelp')}
        </Text>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title={editingId ? t('editPaymentTerm') : t('newPaymentTerm')}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="term_name"
            label={t('termName')}
            rules={[{ required: true, message: t('fieldRequired') }, { max: 100, message: t('termNameMax') }]}
          >
            <Input placeholder={t('termNamePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="days"
            label={t('creditDays')}
            tooltip={t('creditDaysTooltip')}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label={t('documentText')}
            tooltip={t('documentTextTooltip')}
            rules={[{ max: 500, message: t('documentTextMax') }]}
          >
            <Input.TextArea rows={3} placeholder={t('documentTextPlaceholder')} />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PaymentTermsPage;
